import type { ColumnWidthConfig, ColumnWidthPreset } from '../types/column';

/**
 * Width-distribution helpers for the list-builder table.
 *
 * Background (PD-14094): list columns declare an intended size through
 * `widthConfig` (e.g. a Description column is `large`, a numeric column is
 * `small`). That intent was being dropped on the floor — the runtime table only
 * reads `width`/`minWidth`/`maxWidth` and split leftover horizontal space
 * EQUALLY across every flexible column. On wide screens that made compact
 * numeric columns balloon to hundreds of px of whitespace while a wide text
 * column stayed narrow and truncated with an ellipsis even though there was
 * plenty of room.
 *
 * The fix is to translate the preset into a "grow weight" that controls how
 * leftover space is handed out: compact columns (xsmall/small/medium) get a
 * weight of 0 so they DON'T absorb any slack — they sit at their min width —
 * and the slack flows to the wide/absorbing columns (large/xlarge/flexible),
 * which is exactly where long text needs the room. Columns with no preset keep
 * a neutral non-zero weight so preset-less tables still distribute evenly and
 * fill their container (unchanged behavior).
 */

/**
 * Weight used for a column that declares no width preset. Non-zero so that a
 * table with no presets distributes leftover space evenly across all columns —
 * i.e. identical to the previous behavior. Only columns that opt into a compact
 * preset (which get weight 0) change.
 */
export const NEUTRAL_COLUMN_GROW_WEIGHT = 1;

/**
 * `widthConfig` may be either a bare preset string or an object carrying the
 * preset (plus mode/value). Normalize both to the preset.
 */
export function resolveColumnWidthPreset(
	widthConfig: ColumnWidthConfig | undefined,
): ColumnWidthPreset | undefined {
	if (widthConfig == null) {
		return undefined;
	}

	return typeof widthConfig === 'string' ? widthConfig : widthConfig.preset;
}

/**
 * Maps a column's width preset to a grow weight: how much of the table's
 * leftover horizontal space the column should absorb, relative to its peers.
 *
 * Compact presets return 0 — those columns stay at their min width and never
 * balloon. Wide presets return a positive weight so the slack pools into them
 * (a `large` text column ends up absorbing the room that short numeric columns
 * used to waste). When several absorbing columns share a table, the slack
 * splits between them in proportion to these weights. A column with no preset
 * gets {@link NEUTRAL_COLUMN_GROW_WEIGHT} so preset-less tables fill evenly.
 */
export function columnPresetGrowWeight(
	widthConfig: ColumnWidthConfig | undefined,
): number {
	switch (resolveColumnWidthPreset(widthConfig)) {
		// Compact columns — sit at their min width, absorb no slack.
		case 'xsmall':
		case 'small':
		case 'medium':
			return 0;
		// Wide / absorbing columns — soak up the leftover space.
		case 'large':
			return 2;
		case 'xlarge':
		case 'flexible':
			return 3;
		default:
			return NEUTRAL_COLUMN_GROW_WEIGHT;
	}
}

/** Absolute lower bound for any column, so a column never collapses to nothing. */
export const ABSOLUTE_MIN_COLUMN_WIDTH = 40;

export interface ColumnFloorInput {
	/** Measured natural width of the column's header label (px). */
	headerWidth: number;
	/** Measured natural width of the column's widest cell content (px). */
	contentWidth: number;
	/** Configured maxWidth, if any. */
	maxWidth?: number;
	/** Absolute hard floor (defaults to {@link ABSOLUTE_MIN_COLUMN_WIDTH}). */
	absoluteMin?: number;
}

/**
 * The content-first floor a column should occupy before any leftover space is
 * handed out (PD-14094).
 *
 * The historical bug: the table seeded every column at its CONFIGURED `minWidth`
 * (default 120), which never looked at the cell content. A sparse, all-dashes
 * column with `minWidth: 200` still reserved 200px while a long text column
 * truncated and the trailing action column got pushed off-screen.
 *
 * Here the floor is driven by what the column actually needs to render: at least
 * its header label, and its widest content — capped by an explicit `maxWidth`
 * and never below an absolute usability minimum. The configured `minWidth` is
 * intentionally NOT used as the floor: an over-large `minWidth` no longer bloats
 * a sparse column. (`minWidth` is still honored elsewhere as the manual-resize /
 * absolute floor.) The leftover space is then distributed to the wide,
 * grow-weighted columns by the table's existing distributor.
 */
export function computeEffectiveColumnFloor({
	headerWidth,
	contentWidth,
	maxWidth,
	absoluteMin = ABSOLUTE_MIN_COLUMN_WIDTH,
}: ColumnFloorInput): number {
	// A column needs room for the larger of its header label and its content.
	let floor = Math.max(headerWidth, contentWidth);

	// Never wider than an explicit maxWidth...
	if (maxWidth != null) {
		floor = Math.min(floor, maxWidth);
	}

	// ...and never below the absolute usability floor.
	return Math.max(floor, absoluteMin);
}

export interface ShrinkColumnInput {
	key: string;
	/** Natural width the column wants (content-first seed). */
	seed: number;
	/** Narrowest it may go: its header label width. */
	floor: number;
}

/**
 * Fit columns into `available` px when their natural widths do not fit.
 *
 * Each column gives up the same SHARE of its own slack (seed − floor), so a
 * column that needs its width keeps proportionally more of it. The previous
 * rule split the space evenly: a 360px customer name and a 60px number each
 * got the same ~125px, so the name truncated while the number sat in
 * whitespace. Never below a column's floor; when even the floors do not
 * fit, every column sits at its floor and the table scrolls.
 */
export function shrinkColumnsToFit(
	columns: ReadonlyArray<ShrinkColumnInput>,
	available: number,
): Map<string, number> {
	const result = new Map<string, number>();
	const normalized = columns.map((col) => {
		const floor = Math.max(0, col.floor);
		return { key: col.key, floor, seed: Math.max(floor, col.seed) };
	});
	const totalFloor = normalized.reduce((sum, col) => sum + col.floor, 0);
	const totalSlack = normalized.reduce(
		(sum, col) => sum + (col.seed - col.floor),
		0,
	);
	const spare = available - totalFloor;
	const ratio =
		totalSlack <= 0 || spare <= 0 ? 0 : Math.min(1, spare / totalSlack);
	for (const col of normalized) {
		result.set(col.key, Math.floor(col.floor + (col.seed - col.floor) * ratio));
	}
	return result;
}
